import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Space,
  TableProps,
  Tooltip,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import TitleBar from "../../../../components/common/TitleBar";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomDrawer from "../../../../components/ui/CustomDrawer";
import CustomTable from "../../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../../constants/constants";
import {
  AllKPIParams,
  KPI,
} from "../../../../features/organization/contractual/KPISetup/kpiSetup.type";
import {
  useCreateKPIMutation,
  useDeleteKPIMutation,
  useGetAllKPIQuery,
  useUpdateKPIMutation,
} from "../../../../features/organization/contractual/KPISetup/kpiSetupApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import { valueOrNa } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import CalanderIcon from "../../survey/templates/_icons/CalanderIcon";
const { RangePicker } = DatePicker;
const KPIList = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const location = useLocation();
  const { open, handleOpen, handleClose } = useModal<KPI, "DELETE">();
  const queryParams = new URLSearchParams(location.search);
  const title = queryParams.get("title");

  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllKPIParams>();

  // ------ Queries ------
  const { data, isLoading, isFetching } = useGetAllKPIQuery(
    {
      args: { params: { ...filter, search: keyword } },
      kpiGroupId: id as string,
    },
    { skip: !id }
  );

  // ------ Mutations ------
  const [
    deleteKPI,
    {
      isError: isDeleteError,
      error: deleteError,
      isLoading: deleting,
      isSuccess: deleted,
    },
  ] = useDeleteKPIMutation();

  const [
    createKPI,
    {
      isLoading: creating,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
    },
  ] = useCreateKPIMutation();

  const [
    updateKPI,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateKPIMutation();

  const handleSubmit = (values: any) => {
    if (open.type === "ADD") {
      createKPI({ data: values, kpiGroupId: id });
    } else {
      updateKPI({ data: values, kpiGroupId: id, kpiId: open?.data?.id });
    }
  };

  // ------ Effects ------
  useEffect(() => {
    if (open.data) {
      form.setFieldsValue(open.data);
    }
  }, [open.data, form]);

  useEffect(() => {
    if (deleted) {
      handleClose();
      toast("success", "KPI deleted successfully.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  useEffect(() => {
    if (created) {
      handleClose();
      form.resetFields();
      toast("success", "KPI created successfully.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);

  useEffect(() => {
    if (updated) {
      handleClose();
      form.resetFields();
      toast("success", "KPI updated successfully.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  // ------ Errors ------
  useError(isDeleteError, deleteError, "KPI delete failed.");
  useError(isCreateError, createError, "KPI create failed.");
  useError(isUpdateError, updateError, "KPI update failed.");

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "KPI Name",
      dataIndex: "kpi_name",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Weightage",
      dataIndex: "weightage_percentage",
      render: (val) => <span>{valueOrNa(val || 0)}%</span>,
    },
    {
      title: "Threshold",
      dataIndex: "threshold_percentage",
      render: (val) => <span>{valueOrNa(val || 0)}%</span>,
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (record) => (
        <Space align="center">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditIcon />}
              className="action-edit"
              onClick={() => handleOpen({ type: "UPDATE", data: record })}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              icon={<DeleteIcon />}
              className="action-delete"
              onClick={() => handleOpen({ type: "DELETE", data: record })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <SidebarLayout title="KPI Setup">
      <TitleBar backLink={-1 as unknown as string} title={title as string}>
        <Button onClick={() => handleOpen({ type: "ADD" })} type="primary">
          <AddIcon /> New KPI
        </Button>
      </TitleBar>

      <div>
        <CustomTable
          loading={isLoading || isFetching}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data.total}
          dataSource={addKeyInArray(data?.data.kpis)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search"
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={(val) => handleSearch(val)}
              />

              <RangePicker
                style={{ width: 245 }}
                separator="-"
                className="w-full input-bordered"
                format={"DD/MM/YYYY"}
                suffixIcon={<CalanderIcon />}
                placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
                onChange={(dates: (Dayjs | null)[] | null) => {
                  const [start, end] = dates || [];

                  // Call filter only if both dates are selected
                  if (start && end) {
                    handleFilter(
                      "effective_from_end",
                      dayjs(start).format(dbDateFormat)
                    );
                    handleFilter(
                      "effective_from_start",
                      dayjs(end).format(dbDateFormat)
                    );
                  } else {
                    // Clear filters if user clears dates
                    handleFilter("effective_from_end", undefined);
                    handleFilter("effective_from_start", undefined);
                  }
                }}
              />

              <Select
                allowClear
                placeholder="Status"
                className="min-w-[90px]"
                rootClassName="icon-gray"
                popupMatchSelectWidth={120}
                suffixIcon={<SelectDownIcon />}
                onChange={(value) => handleFilter("status", value)}
                options={[
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
            </>
          }
        />
      </div>

      <ConfirmModal
        width={450}
        okLoading={deleting}
        okText="Yes, Delete"
        title="Are you sure?"
        handleClose={() => handleClose()}
        open={open?.open && open.type === "DELETE"}
        handleOk={() => deleteKPI({ kpiId: open.data?.id, kpiGroupId: id })}
        subTitle="This action will permanently delete the selected KPI. This cannot be undone."
      />

      <CustomDrawer
        onClose={() => {
          handleClose();
          form.resetFields();
        }}
        title={open.type === "ADD" ? "Add New Type" : "Edit Type"}
        open={open.open && open.type !== "DELETE"}
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          requiredMark="optional"
          className="custom-form"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="kpi_name"
            label="KPI Name"
            className="mb-4"
            rules={[{ required: true, message: "Please enter kpi name" }]}
          >
            <Input placeholder="Enter KPI name" />
          </Form.Item>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="weightage_percentage"
              label="Weightage (%)"
              className="mb-4"
              rules={[{ required: true, message: "Please enter weightage" }]}
            >
              <Input placeholder="Enter weightage" />
            </Form.Item>
            <Form.Item
              name="threshold_percentage"
              label="Threshold (%)"
              className="mb-4"
              rules={[{ required: true, message: "Please enter threshold" }]}
            >
              <Input placeholder="Enter threshold" />
            </Form.Item>
          </div>
          <Form.Item
            name="description"
            label="Description"
            className="mb-2"
            rules={[
              { required: true, message: "Please enter description" },
              {
                validator: (_, value) => {
                  if (value && value.length > 150) {
                    return Promise.reject(
                      "Description cannot exceed 150 characters"
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={4}
              maxLength={150}
              showCount
              placeholder="Enter description..."
            />
          </Form.Item>
          <div className="text-body-text text-sm font-normal mb-4">
            Tip: clear, simple, and under 150 Characters
          </div>
          <Flex align="center" gap={16} className="mt-6">
            <Button
              size="middle"
              htmlType="button"
              className="min-w-[120px] h-10"
              onClick={() => handleClose()}
            >
              Cancel
            </Button>
            <Button
              size="middle"
              htmlType="submit"
              type="primary"
              color="primary"
              className="min-w-[120px] h-10"
              loading={creating || updating}
            >
              {open.type === "ADD" ? "Add New KPI" : "Update KPI"}
            </Button>
          </Flex>
        </Form>
      </CustomDrawer>
    </SidebarLayout>
  );
};

export default KPIList;
